import { Controller, Control, FieldErrors } from 'react-hook-form';
import { LoanFormData } from '@/schemas/loanSchema';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { getErrorMessage } from '@/components/formErrorMessage';
import USFormattedNumberInput from '@/components/ui/USFormattedNumberInput';
import { DatePicker } from '@/components/ui/date-picker';

interface Props {
  control: Control<LoanFormData>;
  errors: FieldErrors<LoanFormData>;
}

export const LoanStructureSection = ({ control, errors }: Props) => (
  <div className="p-8 mb-8" data-test-id="loan-structure-container">
    <h3 className="section-title" data-test-id="loan-structure-title">
      Loan Structure
    </h3>
    <div className="grid-3" data-test-id="loan-structure-grid">
      <div data-test-id="position-in-capital-stack-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.position_in_capital_stack"
          data-test-id="position-in-capital-stack-label"
        >
          Position in Capital Stack*
        </Label>
        <Controller
          name="loan_structure.position_in_capital_stack"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="position-in-capital-stack-select"
            >
              <SelectTrigger
                className="loanform-select-trigger loanform-select-bg loanform-input-border"
                error={getErrorMessage(errors, 'position_in_capital_stack')}
                data-test-id="position-in-capital-stack-trigger"
              >
                <SelectValue
                  placeholder="Select position"
                  data-test-id="position-in-capital-stack-value"
                />
              </SelectTrigger>
              <SelectContent
                className="loanform-select-content loanform-select-bg loanform-border"
                data-test-id="position-in-capital-stack-content"
              >
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Senior Loan"
                  data-test-id="position-in-capital-stack-option-senior-loan"
                >
                  Senior Loan
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Senior Loan - A Note"
                  data-test-id="position-in-capital-stack-option-senior-loan-a-note"
                >
                  Senior Loan - A Note
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Senior Loan - B Note"
                  data-test-id="position-in-capital-stack-option-senior-loan-b-note"
                >
                  Senior Loan - B Note
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Senior Loan - C Note"
                  data-test-id="position-in-capital-stack-option-senior-loan-c-note"
                >
                  Senior Loan - C Note
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Mezzanine"
                  data-test-id="position-in-capital-stack-option-mezzanine"
                >
                  Mezzanine
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Senior Mezzanine"
                  data-test-id="position-in-capital-stack-option-senior-mezzanine"
                >
                  Senior Mezzanine
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Permanent | Senior Loan - A Note"
                  data-test-id="position-in-capital-stack-option-permanent-senior-a"
                >
                  Permanent | Senior Loan - A Note
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Permanent | Senior Loan - B Note"
                  data-test-id="position-in-capital-stack-option-permanent-senior-b"
                >
                  Permanent | Senior Loan - B Note
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Permanent | Senior Loan - C Note"
                  data-test-id="position-in-capital-stack-option-permanent-senior-c"
                >
                  Permanent | Senior Loan - C Note
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Permanent | Mezzanine"
                  data-test-id="position-in-capital-stack-option-permanent-mezzanine"
                >
                  Permanent | Mezzanine
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Construction | Senior Loan"
                  data-test-id="position-in-capital-stack-option-construction-senior"
                >
                  Construction | Senior Loan
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Construction | Mezzanine"
                  data-test-id="position-in-capital-stack-option-construction-mezzanine"
                >
                  Construction | Mezzanine
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Transitional | Senior Loan"
                  data-test-id="position-in-capital-stack-option-transitional-senior"
                >
                  Transitional | Senior Loan
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="amortization-type-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.amortization_type"
          data-test-id="amortization-type-label"
        >
          Amortization Type*
        </Label>
        <Controller
          name="loan_structure.amortization_type"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="amortization-type-select"
            >
              <SelectTrigger
                error={getErrorMessage(errors, 'amortization_type')}
                data-test-id="amortization-type-trigger"
              >
                <SelectValue
                  placeholder="Select type"
                  data-test-id="amortization-type-value"
                />
              </SelectTrigger>
              <SelectContent data-test-id="amortization-type-content">
                <SelectItem
                  value="Amortizing"
                  data-test-id="amortization-type-option-amortizing"
                >
                  Amortizing
                </SelectItem>
                <SelectItem
                  value="IO"
                  data-test-id="amortization-type-option-io"
                >
                  IO
                </SelectItem>
                <SelectItem
                  value="IOThenAmortizing"
                  data-test-id="amortization-type-option-io-then-amortizing"
                >
                  IOThenAmortizing
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="periodicity-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.periodicity"
          data-test-id="periodicity-label"
        >
          Payment Frequency*
        </Label>
        <Controller
          name="loan_structure.periodicity"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="periodicity-select"
            >
              <SelectTrigger
                error={getErrorMessage(errors, 'periodicity')}
                data-test-id="periodicity-trigger"
              >
                <SelectValue
                  placeholder="Select periodicity"
                  data-test-id="periodicity-value"
                />
              </SelectTrigger>
              <SelectContent data-test-id="periodicity-content">
                <SelectItem
                  value="Monthly"
                  data-test-id="periodicity-option-monthly"
                >
                  Monthly
                </SelectItem>
                <SelectItem
                  value="Quarterly"
                  data-test-id="periodicity-option-quarterly"
                >
                  Quarterly
                </SelectItem>
                <SelectItem
                  value="Semiannual"
                  data-test-id="periodicity-option-semiannual"
                >
                  Semiannual
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="interest-day-count-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.interest_day_count"
          data-test-id="interest-day-count-label"
        >
          Interest Day Count*
        </Label>
        <Controller
          name="loan_structure.interest_day_count"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="interest-day-count-select"
            >
              <SelectTrigger
                error={getErrorMessage(errors, 'interest_day_count')}
                data-test-id="interest-day-count-trigger"
              >
                <SelectValue
                  placeholder="Select day count"
                  data-test-id="interest-day-count-value"
                />
              </SelectTrigger>
              <SelectContent data-test-id="interest-day-count-content">
                <SelectItem
                  value="30/360"
                  data-test-id="interest-day-count-option-30-360"
                >
                  30/360
                </SelectItem>
                <SelectItem
                  value="ACT/360"
                  data-test-id="interest-day-count-option-act-360"
                >
                  ACT/360
                </SelectItem>
                <SelectItem
                  value="ACT/365"
                  data-test-id="interest-day-count-option-act-365"
                >
                  ACT/365
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="io-end-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.io_end_date"
          data-test-id="io-end-date-label"
        >
          IO End Date
        </Label>
        <Controller
          name="loan_structure.io_end_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={field.value ? (typeof field.value === 'string' ? field.value : new Date(field.value).toISOString().split('T')[0]) : ''}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="io-end-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="original-amortization-term-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.original_amortization_term"
          data-test-id="original-amortization-term-label"
        >
          Original Amortization Term
        </Label>
        <Controller
          name="loan_structure.original_amortization_term"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="integer"
              decimals={0}
              step={1}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="original-amortization-term-input"
            />
          )}
        />
      </div>
      <div data-test-id="contractual-pi-payment-amount-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.contractual_pi_payment_amount"
          data-test-id="contractual-pi-payment-amount-label"
        >
          Contractual PI Payment Amount
        </Label>
        <Controller
          name="loan_structure.contractual_pi_payment_amount"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="currency"
              decimals={2}
              step={1000}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="contractual-pi-payment-amount-input"
            />
          )}
        />
      </div>
      <div data-test-id="accrual-type-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.accrual_type"
          data-test-id="accrual-type-label"
        >
          Accrual Type
        </Label>
        <Controller
          name="loan_structure.accrual_type"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="accrual-type-select"
            >
              <SelectTrigger
                error={getErrorMessage(errors, 'accrual_type')}
                data-test-id="accrual-type-trigger"
              >
                <SelectValue
                  placeholder="Select accrual type"
                  data-test-id="accrual-type-value"
                />
              </SelectTrigger>
              <SelectContent data-test-id="accrual-type-content">
                <SelectItem
                  value="Accrued Interest"
                  data-test-id="accrual-type-option-accrued-interest"
                >
                  Accrued Interest
                </SelectItem>
                <SelectItem
                  value="Current Pay"
                  data-test-id="accrual-type-option-current-pay"
                >
                  Current Pay
                </SelectItem>
                <SelectItem
                  value="PIK Interest"
                  data-test-id="accrual-type-option-pik-interest"
                >
                  PIK Interest
                </SelectItem>
                <SelectItem
                  value="PIK Interest & Pay Current"
                  data-test-id="accrual-type-option-pik-pay-current"
                >
                  PIK Interest & Pay Current
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="pik-coupon-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.pik_coupon"
          data-test-id="pik-coupon-label"
        >
          PIK Coupon
        </Label>
        <Controller
          name="loan_structure.pik_coupon"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="pik-coupon-input"
            />
          )}
        />
      </div>
      <div data-test-id="commitment-type-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.commitment_type"
          data-test-id="commitment-type-label"
        >
          Commitment Type*
        </Label>
        <Controller
          name="loan_structure.commitment_type"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="commitment-type-select"
            >
              <SelectTrigger
                error={getErrorMessage(errors, 'commitment_type')}
                data-test-id="commitment-type-trigger"
              >
                <SelectValue
                  placeholder="Select commitment type"
                  data-test-id="commitment-type-value"
                />
              </SelectTrigger>
              <SelectContent data-test-id="commitment-type-content">
                <SelectItem
                  value="Closed End"
                  data-test-id="commitment-type-option-closed-end"
                >
                  Closed End
                </SelectItem>
                <SelectItem
                  value="Open End"
                  data-test-id="commitment-type-option-open-end"
                >
                  Open End
                </SelectItem>
                <SelectItem
                  value="Revolving"
                  data-test-id="commitment-type-option-revolving"
                >
                  Revolving
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="unfunded-commitment-fee-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_structure.unfunded_commitment_fee"
          data-test-id="unfunded-commitment-fee-label"
        >
          Unfunded Commitment Fee
        </Label>
        <Controller
          name="loan_structure.unfunded_commitment_fee"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="unfunded-commitment-fee-input"
            />
          )}
        />
      </div>
    </div>
  </div>
);
